import { useState, useEffect, useCallback } from 'react';
import { useUser, useApi } from 'react-ui';
import { getDraftService, DraftListItem } from '../services/draftService';

interface UseDraftsListOptions {
  autoFetch?: boolean;
}

interface UseDraftsListReturn {
  drafts: DraftListItem[];
  loading: boolean;
  error: string | null;
  fetchDrafts: () => Promise<void>;
  deleteDraft: (draftId: string) => Promise<boolean>;
  refetch: () => Promise<void>;
}

/**
 * Hook for fetching and managing the user's drafts list
 */
export function useDraftsList(options: UseDraftsListOptions = {}): UseDraftsListReturn {
  const { autoFetch = true } = options;
  
  const [drafts, setDrafts] = useState<DraftListItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const { user } = useUser();
  const { apiEndpoints, apiKey } = useApi();

  // Get token from user object
  const token = user?.token;

  const apiEndpoint = apiEndpoints?.metadata 
    ? new URL(apiEndpoints.metadata).origin 
    : '';

  const fetchDrafts = useCallback(async () => {
    if (!user || !token || !apiEndpoint || !apiKey) {
      console.log('[useDraftsList] Skipping fetch - not authenticated');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const service = getDraftService(apiEndpoint, apiKey);
      const response = await service.getDrafts(token);
      setDrafts(response.drafts);
      console.log('[useDraftsList] Fetched drafts:', response.total);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch drafts';
      setError(message);
      console.error('[useDraftsList] Error:', err);
    } finally {
      setLoading(false);
    }
  }, [user, token, apiEndpoint, apiKey]);

  const deleteDraft = useCallback(async (draftId: string): Promise<boolean> => {
    if (!token || !apiEndpoint || !apiKey) {
      return false;
    }

    try {
      const service = getDraftService(apiEndpoint, apiKey);
      await service.deleteDraft(token, draftId);
      
      // Remove from local state
      setDrafts(prev => prev.filter(d => d.id !== draftId));
      console.log('[useDraftsList] Deleted draft:', draftId);
      return true;
    } catch (err) {
      console.error('[useDraftsList] Delete error:', err);
      return false;
    }
  }, [token, apiEndpoint, apiKey]);

  // Auto-fetch on mount if enabled
  useEffect(() => {
    if (autoFetch && user && token) {
      fetchDrafts();
    }
  }, [autoFetch, user, token, fetchDrafts]);

  return {
    drafts,
    loading,
    error,
    fetchDrafts,
    deleteDraft,
    refetch: fetchDrafts,
  };
}
